import {
  createFormGroupState,
  FormGroupState,
  onNgrxForms,
  updateGroup,
  validate,
  wrapReducerWithFormStateUpdate,
} from 'ngrx-forms';
import { email, minLength, pattern, required } from 'ngrx-forms/validation';
import { fieldEqualTo } from '@ellf/app-forms';
import { User } from '@ellf/app-shared-user';
import { createReducer, on } from '@ngrx/store';

import * as AuthActions from './auth.actions';

export const AUTH_FEATURE_KEY = 'auth';

export const LOGIN_FORM_ID = 'loginForm';
export const PROFILE_CHANGE_PASSWORD_FORM_ID = 'profileChangePasswordForm';
export const FORGOT_PASSWORD_FORM_ID = 'forgotPasswordForm';
export const RESET_PASSWORD_FORM_ID = 'resetPasswordForm';
export interface LoginFormValue {
  username: string;
  password: string;
  rememberMe: boolean;
  newPassword: string;
  confirmPassword: string;
}
export interface ForgotPasswordFormValue {
  username: string;
}
export interface ResetPasswordFormValue {
  newPassword: string;
  confirmPassword: string;
}

export interface AuthState {
  loginForm: FormGroupState<LoginFormValue>;
  loggedUser?: User;
  forgotPasswordForm: FormGroupState<ForgotPasswordFormValue>;
  resetPasswordForm: FormGroupState<ResetPasswordFormValue>;
  authError: string;
  resetPasswordEmailSent: boolean;
  userMustResetPassword: boolean;
}

export interface AuthPartialState {
  readonly [AUTH_FEATURE_KEY]: AuthState;
}

export const loginFormInitialState: LoginFormValue = {
  username: '',
  password: '',
  rememberMe: false,
  newPassword: '',
  confirmPassword: '',
};

export const forgotPasswordFormInitialState: ForgotPasswordFormValue = {
  username: '',
};

export const resetPasswordFormInitialState: ResetPasswordFormValue = {
  newPassword: '',
  confirmPassword: '',
};

const initialState: AuthState = {
  // set initial required properties
  loginForm: createFormGroupState<LoginFormValue>(
    LOGIN_FORM_ID,
    loginFormInitialState,
  ),
  loggedUser: undefined,
  userMustResetPassword: false,
  resetPasswordEmailSent: false,
  forgotPasswordForm: createFormGroupState<ForgotPasswordFormValue>(
    FORGOT_PASSWORD_FORM_ID,
    forgotPasswordFormInitialState,
  ),
  resetPasswordForm: createFormGroupState<ResetPasswordFormValue>(
    FORGOT_PASSWORD_FORM_ID,
    resetPasswordFormInitialState,
  ),
  authError: '',
};

const validateLoginForm = (
  formState: FormGroupState<LoginFormValue>,
  state: AuthState,
) => {
  return updateGroup(formState, {
    username: (controlState, parentState) => {
      controlState = validate(controlState, required, email);
      return controlState;
    },
    password: (controlState, parentState) => {
      controlState = validate(controlState, required);
      return controlState;
    },
    newPassword: (controlState, parentState) => {
      if (state.userMustResetPassword) {
        controlState = validate(controlState, required, minLength(8));

        if (controlState.isValid) {
          controlState = validate(
            controlState,
            pattern(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*_-])(?=.{8,})/,
            ),
          );
        }
      }
      return controlState;
    },
    confirmPassword: (controlState, parentState) => {
      if (state.userMustResetPassword) {
        controlState = validate(
          controlState,
          fieldEqualTo(
            parentState.value.newPassword,
            'Confirm password',
            'New Password',
          ),
        );
      }
      return controlState;
    },
  });
};

const validateForgotPasswordForm = updateGroup<ForgotPasswordFormValue>({
  username: validate(required, email),
});

const validateResetPasswordForm = (
  formState: FormGroupState<ResetPasswordFormValue>,
) => {
  return updateGroup(formState, {
    newPassword: (controlState, parentState) => {
      controlState = validate(controlState, required, minLength(8));

      if (controlState.isValid) {
        controlState = validate(
          controlState,
          pattern(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
          ),
        );
      }
      return controlState;
    },
    confirmPassword: (controlState, parentState) => {
      controlState = validate(
        controlState,
        fieldEqualTo(
          parentState.value.newPassword,
          'Confirm password',
          'New Password',
        ),
      );
      return controlState;
    },
  });
};

const rawReducer = createReducer(
  initialState,
  onNgrxForms(),
  on(AuthActions.getLoggedUserSuccess, (state, { loggedUser }) => {
    return {
      ...state,
      loggedUser,
    };
  }),
  on(AuthActions.logout, (state) => {
    return {
      ...state,
      loggedUser: undefined,
    };
  }),
  on(AuthActions.setUserMustResetPassword, (state) => {
    return { ...state, userMustResetPassword: true };
  }),
  on(AuthActions.loginSuccess, (state) => {
    return { ...state, userMustResetPassword: false, authError: '' };
  }),
  on(AuthActions.loginFailure, (state, { error }) => {
    return { ...state, authError: error };
  }),
  // on(AuthActions.forgotPasswordSuccess, (state) => {
  //   return { ...state, resetPasswordEmailSent: true };
  // }),
  on(AuthActions.resetPasswordSuccess, (state) => {
    return { ...state, authError: '' };
  }),
  // on(AuthActions.resetPasswordFailure, (state, { error }) => {
  //   return { ...state, authError: error };
  // }),
  on(AuthActions.resetAuthError, (state) => {
    return { ...state, authError: '' };
  }),
);

export const reducer = wrapReducerWithFormStateUpdate(
  wrapReducerWithFormStateUpdate(
    wrapReducerWithFormStateUpdate(
      rawReducer,
      // point to the form state to update
      (s) => s.resetPasswordForm,
      // this function is always called after the reducer
      validateResetPasswordForm,
    ),
    // point to the form state to update
    (s) => s.forgotPasswordForm,
    // this function is always called after the reducer
    validateForgotPasswordForm,
  ),
  // point to the form state to update
  (s) => s.loginForm,
  // this function is always called after the reducer
  validateLoginForm,
);
